import { ChangeDetectionStrategy, Component, booleanAttribute, EventEmitter, Input, Output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { HeaderPaddingSafeAreaComponent } from '../../@components/header-padding-safe-area/header-padding-safe-area.component';

/**
 * En-tête commun des assistants (ajout de plante, guide, ajout de graines,
 * nouvelle zone, onboarding, plan de saison), repris du plan de saison.
 *
 * Une barre de 48 px toujours présente : retour à gauche (vide à la première
 * étape, la place reste réservée pour que rien ne saute d'une étape à
 * l'autre), titre centré, croix à droite. Les deux boutons font 44 px et ont
 * un libellé pour le lecteur d'écran. Dessous, « Étape n sur N » et la barre
 * d'avancement, quand l'assistant compte ses étapes.
 *
 * Usage :
 * ```html
 * <vw-wizard-header title="garden.add-plant.wizzard.title" [current]="2" [total]="6"
 *   [showBack]="index > 0" (back)="previous()" (close)="cancel()" />
 * ```
 */
@Component({
  selector: 'vw-wizard-header',
  standalone: true,
  imports: [TranslatePipe, HeaderPaddingSafeAreaComponent],
  templateUrl: './wizard-header.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  // Collant sur l'hôte : sur l'élément intérieur, le collage resterait enfermé dans l'hôte.
  // `title="clé"` écrit en dur laisse aussi l'attribut HTML sur l'hôte : sur ordinateur, son
  // survol affichait la clé brute en infobulle. On le retire (l'entrée garde sa valeur).
  host: { class: 'sticky top-0 z-20 block bg-vw-surface print:hidden', '[attr.title]': 'null' },
})
export class WizardHeaderComponent {
  /** Titre (clé i18n ou texte). Vide : la ligne reste, sans titre. */
  @Input() title = '';
  /** Étape courante, comptée à partir de 1. Sans elle, ni compteur ni barre. */
  @Input() current: number | null = null;
  /** Nombre d'étapes. */
  @Input() total: number | null = null;
  /** Bouton retour (absent à la première étape ; sa place reste réservée). */
  @Input({ transform: booleanAttribute }) showBack = false;
  /** Croix de fermeture (absente quand on ne doit pas sortir, comme l'onboarding). */
  @Input({ transform: booleanAttribute }) showClose = true;
  /** Bande du haut de l'écran (zone de l'encoche) ; à couper quand la page la pose déjà. */
  @Input({ transform: booleanAttribute }) safeArea = true;
  /** Libellé du retour pour le lecteur d'écran (clé i18n). */
  @Input() backLabelKey = 'common.back';
  /** Libellé de la croix pour le lecteur d'écran (clé i18n). */
  @Input() closeLabelKey = 'common.close';

  @Output() back = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();

  get hasProgress(): boolean {
    return !!this.current && !!this.total;
  }

  get progressPercent(): number {
    return this.hasProgress ? Math.min(100, (this.current! / this.total!) * 100) : 0;
  }
}
